"use client";

import { useReducedMotion } from "framer-motion";

import { useIsMobile } from "@/hooks/useIsMobile";

/**
 * The width the page stops animating below.
 *
 * Deliberately the same 992px the layouts break at, so "static" and "stacked in
 * flow" are one state rather than two that overlap. Everything the motion here
 * is built around — the scatter, the year rail, the two-column entries, the
 * photo travelling out of the principles column — only exists above it. Below,
 * the reveals were animating content that had already been re-laid-out into a
 * single column, which is a cost with nothing on the other side of it.
 */
export const MOTION_BREAKPOINT = 992;

/**
 * The page's one motion decision, so the sections can't drift apart on it.
 *
 * `reduceMotion` is the off switch: reveals resolve to their finished state,
 * counters print their authored figure, the odometer renders as text and the
 * load sequence collapses to a plain fade. `parallax` is the narrower one —
 * the drifts, which need a layout that holds still relative to itself.
 *
 * Both read `isMobile === true` rather than `!== false` on purpose.
 * `useIsMobile` reports `null` until its media queries resolve, which on the
 * server is always, so `null` has to mean something. Treating it as "not mobile"
 * keeps the markup React sends identical to what a desktop client paints, and
 * leaves the correction to the client — where `useIsMobile` runs in a layout
 * effect, so a phone has the right answer before its first paint.
 */
export function useMotionMode() {
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile({ breakpoint: MOTION_BREAKPOINT });

  const reduceMotion = prefersReducedMotion === true || isMobile === true;

  return { reduceMotion, parallax: !reduceMotion && isMobile === false };
}
